import { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { FilterableField } from './types';
import { getFieldIcon } from './icons';

interface AttributeSelectorProps {
  fields: FilterableField[];
  onSelect: (field: FilterableField) => void;
  onClose: () => void;
}

export function AttributeSelector({ fields, onSelect, onClose }: AttributeSelectorProps) {
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const filteredFields = fields.filter((field) =>
    field.label.toLowerCase().includes(search.toLowerCase())
  );

  // Group fields: standard by type, custom separate
  const standardFields = filteredFields.filter((f) => !f.custom);
  const customFields = filteredFields.filter((f) => f.custom);

  const textFields = standardFields.filter((f) => f.type === 'text');
  const dateFields = standardFields.filter((f) => f.type === 'date');
  const numericFields = standardFields.filter((f) => f.type === 'numeric');

  const renderField = (field: FilterableField) => {
    const Icon = getFieldIcon(field);
    return (
      <button
        type="button"
        key={field.key}
        onClick={() => onSelect(field)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
      >
        <Icon className="w-4 h-4 text-gray-500" />
        <span>{field.label}</span>
      </button>
    );
  };

  return (
    <div
      ref={containerRef}
      className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
    >
      <div className="p-2 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar atributos..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {textFields.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50">
              Dados do Usuário
            </div>
            {textFields.map(renderField)}
          </div>
        )}

        {dateFields.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50">
              Datas
            </div>
            {dateFields.map(renderField)}
          </div>
        )}

        {numericFields.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50">
              Atividade
            </div>
            {numericFields.map(renderField)}
          </div>
        )}

        {customFields.length > 0 && (
          <div>
            <div className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50">
              Dados Personalizados
            </div>
            {customFields.map(renderField)}
          </div>
        )}

        {filteredFields.length === 0 && (
          <div className="px-3 py-4 text-sm text-gray-500 text-center">
            Nenhum atributo encontrado
          </div>
        )}
      </div>
    </div>
  );
}
